import { AddCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { Context } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateImpregnationCache, useDeleteImpregnation } from 'src/api/impregnation'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { MainComponentStatus } from 'src/enums'
import { permissionValue } from 'src/enums/permissionValue'
import { IImpregnation } from 'src/generated-types/impregnation'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { Padding } from 'src/styles/paddingComponent'
import { getThemeColor } from 'src/utils'
import ImpregnationTable from './ImpregnationTable'

interface ImpregnationTabProps {
  mainComponentId: EntityId
  context: Context<any> | null | undefined
  mainComponentStatus: MainComponentStatus
  serviceStationId?: EntityId
  withoutLayout?: boolean
  onAddImpregnation?: (initialValues: IImpregnation | undefined) => void
  height?: string
  autoHeight?: boolean
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
  setDataLength?: (length: number) => void
  disabled?: boolean
}
const ImpregnationTab = ({
  mainComponentId,
  context,
  mainComponentStatus,
  withoutLayout,
  onAddImpregnation,
  height,
  autoHeight = false,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
  setDataLength,
  disabled,
}: ImpregnationTabProps) => {
  const { t } = useTranslation(['common'])
  const theme = useTheme()
  const { mutateAsync: deleteImpregnation } = useDeleteImpregnation(mainComponentId as EntityId)
  const { showConfirmDialog } = useDialogCtx()

  const handleDeleteImpregnation = async (impregnationId: EntityId) => {
    showConfirmDialog(
      t('delete-impregnation', { ns: 'impregnation' }),
      t('are-you-sure-delete-impregnation', { ns: 'impregnation' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await deleteImpregnation(impregnationId, {
          onSuccess: () => {
            toast.success(t('impregnation-deleted-successfully', { ns: 'impregnation' }))
            invalidateImpregnationCache.getImpregnations(mainComponentId as EntityId)
            invalidationActivityLogCache.getMainComponentActivityLogs({
              mainComponentId: mainComponentId,
            })
            // TODO(cache): ImpregnationTab receives no positionId/cagePositionId prop,
            // so it cannot invalidate getCageActivityLogs or getCageMaintenanceLogs with
            // a cage-scoped key. Reviewer noted "no cage-level invalidation at all" —
            // this is intentional given the component's current interface. If a
            // positionId prop is added in the future, mirror the WashingReports pattern
            // and add both getCageActivityLogs and getCageMaintenanceLogs calls here.
            pop()
          },
          onError: () => {
            toast.error(t('error-while-deleting-impregnation', { ns: 'impregnation' }))
            pop()
          },
        })
      },
    )
  }

  const ImpregnationTableOnly = (
    <ImpregnationTable
      context={context}
      mainComponentStatus={mainComponentStatus}
      mainComponentId={mainComponentId}
      editImpregnation={onAddImpregnation}
      deleteImpregnation={handleDeleteImpregnation}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      minimalEmptyStatus={minimalEmptyStatus}
      setDataLength={setDataLength}
      disabled={disabled}
    />
  )

  if (withoutLayout) {
    return ImpregnationTableOnly
  }

  return (
    <Padding.pt6>
      <CommonPageLayout
        titleSection={{ subtitle: t('impregnations', { ns: 'impregnation' }) }}
        topAction={
          mainComponentStatus == MainComponentStatus.InService
            ? [
                {
                  buttonText: t('add-impregnation', { ns: 'impregnation' }),
                  buttonIcon: <AddCircleOutline />,
                  onClick: onAddImpregnation,
                  variant: 'outlined',
                  shadowWeight: null,
                  permissionBinaryValues: [permissionValue.ServiceStation.Write],
                  context: context,
                  disabled: mainComponentStatus != MainComponentStatus.InService || disabled,
                },
              ]
            : []
        }
        childrenProps={{ bgcolor: getThemeColor(theme, MUIThemeColors.white) }}
        titleContainerProps={{ paddingBottom: '12px' }}
      >
        <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
          {ImpregnationTableOnly}
        </Box>
      </CommonPageLayout>
    </Padding.pt6>
  )
}

export default ImpregnationTab
